import { useInterfaceStore } from '@/store/interface';
import { POSSIBLE_SOURCES, CODEMETA_SOURCE } from './partyManagerUtils';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePartyManager } from './PartyManagerContext';
import { useEffect } from 'react';

const ALL_SELECTABLE_SOURCES = {
  codemeta: CODEMETA_SOURCE,
  ...POSSIBLE_SOURCES,
};

const getIconForSource = (sourceKey) => {
  const sourceInfo = ALL_SELECTABLE_SOURCES[sourceKey];
  if (!sourceInfo) return '?';

  if (typeof sourceInfo.icon === 'string') {
    return (
      <img
        src={sourceInfo.icon}
        alt={sourceInfo.name}
        style={{ width: '20px', height: '20px' }}
      />
    );
  }
  return sourceInfo.icon;
};

const TargetSystemSelector = ({
  sourcesToHandle,
  onSourceToggle,
  setSourcesToHandle,
  weclappDisabled = false,
  isNewParty = false,
}) => {
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { partyData } = usePartyManager();
  const availableSources = Object.keys(ALL_SELECTABLE_SOURCES).filter(
    (sourceKey) => sourceKey === 'codemeta' || !!INTERFACE_STORE[sourceKey],
  );

  const selectedAssignments =
    partyData.assignedOrganizations[partyData.assignedOrganizations.selected] ||
    [];

  const isPrivateCustomer =
    partyData?.partyType === 'PERSON' &&
    (partyData.isCustomer?.edit || partyData.isCustomer?.selected === 'edit'
      ? partyData.isCustomer.edit
      : partyData.isCustomer?.[partyData.isCustomer?.selected]);

  const showTanssWarning =
    isNewParty &&
    partyData?.partyType === 'PERSON' &&
    selectedAssignments?.length === 0 &&
    !isPrivateCustomer;

  const selectedSources = availableSources.filter(
    (key) => sourcesToHandle[key],
  );

  useEffect(() => {
    if (
      isNewParty &&
      partyData?.partyType === 'PERSON' &&
      selectedAssignments?.length === 0 &&
      !isPrivateCustomer
    ) {
      setSourcesToHandle((prev) => ({
        ...prev,
        tanss: false,
      }));
    }
    if (weclappDisabled) {
      setSourcesToHandle((prev) => ({
        ...prev,
        weclapp: false,
      }));
    }
  }, [
    isNewParty,
    partyData?.partyType,
    selectedAssignments,
    weclappDisabled,
    isPrivateCustomer,
  ]);

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        Zieldatensysteme
      </Typography>
      <ToggleButtonGroup
        value={selectedSources.filter(
          (k) =>
            !(k === 'tanss' && showTanssWarning) &&
            !(k === 'weclapp' && weclappDisabled),
        )}
        aria-label="Zieldatensysteme auswählen"
        size="small"
        sx={{ mt: 1, width: '100%', display: 'flex' }}
      >
        {availableSources.map((sourceKey) => {
          const sourceInfo = ALL_SELECTABLE_SOURCES[sourceKey];
          const isTanssWarning = sourceKey === 'tanss' && showTanssWarning;
          const isWeclappWarning = sourceKey === 'weclapp' && weclappDisabled;

          let tooltipText = sourceInfo.name;
          if (isTanssWarning) {
            tooltipText = `Zum Synchronisieren von TANSS-Daten muss eine Organisation ausgewählt sein oder es muss sich um einen Privatkunden handeln.`;
          } else if (isWeclappWarning) {
            tooltipText = `Zum Synchronisieren mit Weclapp muss die Person bei mindestens einer Firma als Ansprechpartner markiert sein.`;
          }

          return (
            <Tooltip key={sourceKey} title={tooltipText}>
              <span style={{ flex: 1, display: 'flex' }}>
                <ToggleButton
                  value={sourceKey}
                  disabled={
                    sourceKey === 'codemeta' ||
                    isTanssWarning ||
                    isWeclappWarning
                  }
                  onClick={() => onSourceToggle(sourceKey)}
                  sx={{
                    flex: 1,
                    width: '100%',
                  }}
                  color={
                    ALL_SELECTABLE_SOURCES[sourceKey] ? 'primary' : 'default'
                  }
                >
                  {getIconForSource(sourceKey)}
                  {(isTanssWarning || isWeclappWarning) && (
                    <WarningAmberIcon color="warning" />
                  )}
                </ToggleButton>
              </span>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};

export default TargetSystemSelector;
